import { useStrings } from "@1js/localization-components";
import type { Prompt } from "@1js/pl-card-components";
import { isCopilotChatPrompt, isWXPPrompt } from "@1js/pl-types";
import { Link } from "@fluentui/react-components";
import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { ManageAccessDialogTitle } from "../../components/ManageSharedPrompts/ManageSharedPrompts.strings";
import useTelemetry from "../../utils/Telemetry/useTelemetry";
import { useFlightContext } from "../useFlightContext";
import {
  ShareToastCopyLinkBody,
  ShareToastCopyLinkTitle,
  ShareToastEmailLinkTitle,
  TryAgainLabel,
} from "./useShareToast.strings";
import { useToast } from "./useToast";

/**
 * Hook to show a toast when the user copies the prompt
 */
export const useShareToast = (...props: Parameters<typeof useToast>) => {
  const { notify, info } = useToast(...props);
  const telemetry = useTelemetry();
  const strings = useStrings({
    TryAgainLabel,
    ShareToastCopyLinkTitle,
    ShareToastCopyLinkBody,
    ShareToastEmailLinkTitle,
    ManageAccessDialogTitle,
  });
  const { isFlightActive } = useFlightContext();

  const [searchParams, setSearchParams] = useSearchParams();

  const openDialog = React.useCallback(() => {
    telemetry.sendUINotificationsActionEvent("LinkClicked", "View Settings");
    searchParams.set("manageAccess", "open");
    setSearchParams(searchParams);
  }, [telemetry, searchParams, setSearchParams]);

  const toastBody = (prompt: Prompt) => {
    return isFlightActive("isPromptSharingEnabled") &&
      (isCopilotChatPrompt(prompt) || isWXPPrompt(prompt)) &&
      prompt.Origin === "User"
      ? strings.ShareToastCopyLinkBody()
      : undefined;
  };

  const notifyShareCopyLinkSuccess = (prompt: Prompt) => {
    const showManagedAccessLink = prompt.Origin === "User";
    if (showManagedAccessLink) {
      telemetry.sendUINotificationsActionEvent("Open", "View Settings");
    }

    notify(
      "success",
      strings.ShareToastCopyLinkTitle(),
      toastBody(prompt),
      showManagedAccessLink ? (
        <Link onClick={openDialog} data-testid={"manage-access-link"}>
          {strings.ManageAccessDialogTitle()}
        </Link>
      ) : undefined
    );
  };

  const notifyShareEmailLinkSuccess = (prompt: Prompt) => {
    const showManagedAccessLink = prompt.Origin === "User";
    if (showManagedAccessLink) {
      telemetry.sendUINotificationsActionEvent("Open", "View Settings");
    }

    info(
      strings.ShareToastEmailLinkTitle(),
      toastBody(prompt),
      showManagedAccessLink ? (
        <Link onClick={openDialog}> {strings.ManageAccessDialogTitle()}</Link>
      ) : undefined,
      // Don't pause on window blur for email toast because the email tab will be opened in a new window
      false
    );
  };

  const notifyShareCopyLinkFail = () =>
    notify("error", strings.TryAgainLabel());

  return {
    notifyShareCopyLinkSuccess,
    notifyShareEmailLinkSuccess,
    notifyShareCopyLinkFail,
  };
};
